import React, { useEffect, useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  countVisitesByDate,
  deleteVisitesByDate,
} from "../../../../../services/visitesServices";

import {
  FaBroom,
  FaChartLine,
  FaCheck,
  FaSyncAlt,
  FaTimes,
  FaTrash,
} from "react-icons/fa";

/* -------------------------------------------------------------------------- */
/* Helpers dates                                                              */
/* -------------------------------------------------------------------------- */

const parseYMD = (ymd) => {
  const [year, month, day] = String(ymd).split("-").map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
};

const ymdFromDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const startOfToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const formatShortDate = (dateValue) => {
  const date =
    typeof dateValue === "string" ? parseYMD(dateValue) : new Date(dateValue);

  if (Number.isNaN(date.getTime())) return dateValue;

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
  });
};

const formatLongDate = (dateValue) => {
  const date =
    typeof dateValue === "string" ? parseYMD(dateValue) : new Date(dateValue);

  if (Number.isNaN(date.getTime())) return dateValue;

  return date.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const fillMissingDates = (rows) => {
  const arr = Array.isArray(rows) ? rows : [];

  if (!arr.length) {
    const today = startOfToday();
    return [
      {
        date_visite: ymdFromDate(today),
        compteur: 0,
      },
    ];
  }

  const sorted = arr
    .map((row) => ({
      date_visite: String(row.date_visite),
      compteur: Number(row.compteur || 0),
    }))
    .sort((a, b) => a.date_visite.localeCompare(b.date_visite));

  const byDate = new Map(
    sorted.map((row) => [row.date_visite, Number(row.compteur || 0)])
  );

  const firstDate = parseYMD(sorted[0].date_visite);
  const lastDataDate = parseYMD(sorted[sorted.length - 1].date_visite);
  const today = startOfToday();

  const endDate = today > lastDataDate ? today : lastDataDate;

  const output = [];

  for (
    let current = new Date(firstDate);
    current <= endDate;
    current.setDate(current.getDate() + 1)
  ) {
    const key = ymdFromDate(current);

    output.push({
      date_visite: key,
      compteur: byDate.get(key) ?? 0,
    });
  }

  return output;
};

const sum = (arr, selector = (item) => item) =>
  arr.reduce((acc, item) => acc + Number(selector(item) || 0), 0);

const movingAverage = (rows, windowSize = 7) => {
  return rows.map((row, index) => {
    const start = Math.max(0, index - windowSize + 1);
    const slice = rows.slice(start, index + 1);
    const average = slice.length ? sum(slice, (item) => item.compteur) / slice.length : 0;

    return {
      ...row,
      compteur_lisse: Math.round(average * 10) / 10,
    };
  });
};

function StatMiniCard({ label, value, helper }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/60">
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
        {label}
      </p>

      <p className="mt-1 text-2xl font-black text-slate-950 dark:text-white">
        {value}
      </p>

      {helper ? (
        <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
          {helper}
        </p>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export default function VisitesLineChart() {
  const [rawData, setRawData] = useState([]);

  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [range, setRange] = useState("30");
  const [smooth, setSmooth] = useState(false);

  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const [cleanerOpen, setCleanerOpen] = useState(false);
  const [deleteStart, setDeleteStart] = useState("");
  const [deleteEnd, setDeleteEnd] = useState("");

  const fetchVisites = async () => {
    try {
      setLoading(true);
      setErrorMsg("");

      const response = await countVisitesByDate();
      const rows = Array.isArray(response) ? response : [];

      setRawData(fillMissingDates(rows));
    } catch (error) {
      console.error("Erreur lors de la récupération des visites :", error);
      setRawData([]);
      setErrorMsg("Impossible de charger les statistiques de visites.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVisites();
  }, []);

  const filteredData = useMemo(() => {
    let rows = Array.isArray(rawData) ? rawData : [];

    if (range === "custom") {
      if (customStart) {
        rows = rows.filter((row) => row.date_visite >= customStart);
      }

      if (customEnd) {
        rows = rows.filter((row) => row.date_visite <= customEnd);
      }
    } else if (range !== "all") {
      const days = Number(range);
      rows = rows.slice(Math.max(0, rows.length - days));
    }

    const enhanced = movingAverage(rows, 7);

    return enhanced.map((row) => ({
      ...row,
      date_label: formatShortDate(row.date_visite),
      value: smooth ? row.compteur_lisse : row.compteur,
    }));
  }, [rawData, range, customStart, customEnd, smooth]);

  const kpis = useMemo(() => {
    const total = sum(filteredData, (item) => item.compteur);
    const days = filteredData.length;
    const average = days ? Math.round((total / days) * 10) / 10 : 0;
    const today = filteredData[filteredData.length - 1]?.compteur || 0;
    const max = filteredData.length
      ? Math.max(...filteredData.map((item) => Number(item.compteur || 0)))
      : 0;

    const from = filteredData[0]?.date_visite || null;
    const to = filteredData[filteredData.length - 1]?.date_visite || null;

    return {
      total,
      days,
      average,
      today,
      max,
      from,
      to,
    };
  }, [filteredData]);

  const handleDelete = async () => {
    setErrorMsg("");
    setSuccessMsg("");

    if (!deleteStart || !deleteEnd) {
      setErrorMsg("Veuillez sélectionner une date de début et une date de fin.");
      return;
    }

    if (deleteStart > deleteEnd) {
      setErrorMsg("La date de début doit être antérieure à la date de fin.");
      return;
    }

    const confirmDelete = window.confirm(
      `Supprimer les visites du ${formatLongDate(deleteStart)} au ${formatLongDate(
        deleteEnd
      )} ?`
    );

    if (!confirmDelete) return;

    try {
      setDeleteLoading(true);

      const response = await deleteVisitesByDate(deleteStart, deleteEnd);

      setSuccessMsg(
        response?.message || "Les visites ont été supprimées avec succès."
      );

      setDeleteStart("");
      setDeleteEnd("");
      setCleanerOpen(false);

      await fetchVisites();
    } catch (error) {
      console.error("Erreur suppression visites :", error);
      setErrorMsg("Une erreur est survenue lors de la suppression des visites.");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header + actions */}
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
              <FaChartLine />
            </span>

            <div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                Évolution des visites
              </h3>

              <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">
                Analyse du nombre de visites par jour, avec période filtrable et
                option de lissage.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={fetchVisites}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaSyncAlt className={loading ? "animate-spin" : ""} />
            Actualiser
          </button>

          <button
            type="button"
            onClick={() => setCleanerOpen((prev) => !prev)}
            className={`inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-bold shadow-sm transition ${
              cleanerOpen
                ? "bg-rose-600 text-white hover:bg-rose-700"
                : "border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300 dark:hover:bg-rose-500/20"
            }`}
          >
            {cleanerOpen ? <FaTimes /> : <FaBroom />}
            Nettoyer
          </button>
        </div>
      </div>

      {/* Filtres */}
      <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/50">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-wrap gap-2">
            {[
              { id: "7", label: "7 jours" },
              { id: "30", label: "30 jours" },
              { id: "90", label: "90 jours" },
              { id: "all", label: "Tout" },
              { id: "custom", label: "Période" },
            ].map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => setRange(option.id)}
                className={`rounded-2xl px-3 py-2 text-sm font-bold transition ${
                  range === option.id
                    ? "bg-indigo-600 text-white shadow-sm"
                    : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          <label className="inline-flex cursor-pointer items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
            <input
              type="checkbox"
              checked={smooth}
              onChange={(event) => setSmooth(event.target.checked)}
              className="h-4 w-4 accent-indigo-600"
            />
            Lisser la courbe sur 7 jours
          </label>
        </div>

        {range === "custom" ? (
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                Date de début
              </label>
              <input
                type="date"
                value={customStart}
                onChange={(event) => setCustomStart(event.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                Date de fin
              </label>
              <input
                type="date"
                value={customEnd}
                onChange={(event) => setCustomEnd(event.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
              />
            </div>
          </div>
        ) : null}
      </div>

      {/* Nettoyage */}
      {cleanerOpen ? (
        <div className="rounded-[24px] border border-rose-200 bg-rose-50 p-4 dark:border-rose-500/20 dark:bg-rose-500/10">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <p className="text-sm font-black text-rose-700 dark:text-rose-300">
                Supprimer des visites
              </p>

              <p className="mt-1 text-sm leading-6 text-rose-700/80 dark:text-rose-200/80">
                Les dates sont inclusives. Après suppression, le graphique est
                automatiquement rafraîchi.
              </p>
            </div>

            <div className="grid flex-1 gap-3 sm:grid-cols-[1fr_1fr_auto] xl:max-w-3xl">
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-[0.16em] text-rose-700 dark:text-rose-300">
                  Début
                </label>
                <input
                  type="date"
                  value={deleteStart}
                  onChange={(event) => setDeleteStart(event.target.value)}
                  disabled={deleteLoading}
                  className="w-full rounded-2xl border border-rose-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-rose-400 disabled:opacity-60 dark:border-rose-500/20 dark:bg-slate-900 dark:text-white"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-[0.16em] text-rose-700 dark:text-rose-300">
                  Fin
                </label>
                <input
                  type="date"
                  value={deleteEnd}
                  onChange={(event) => setDeleteEnd(event.target.value)}
                  disabled={deleteLoading}
                  className="w-full rounded-2xl border border-rose-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-rose-400 disabled:opacity-60 dark:border-rose-500/20 dark:bg-slate-900 dark:text-white"
                />
              </div>

              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteLoading}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-rose-600 px-4 py-2 text-sm font-black text-white shadow-sm transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60 sm:self-end"
              >
                {deleteLoading ? (
                  <>
                    <FaSyncAlt className="animate-spin" />
                    Suppression
                  </>
                ) : (
                  <>
                    <FaTrash />
                    Supprimer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {errorMsg ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
          {errorMsg}
        </div>
      ) : null}

      {successMsg ? (
        <div className="flex items-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300">
          <FaCheck />
          {successMsg}
        </div>
      ) : null}

      {/* KPIs */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatMiniCard
          label="Période"
          value={
            kpis.from && kpis.to
              ? `${formatShortDate(kpis.from)} → ${formatShortDate(kpis.to)}`
              : "—"
          }
          helper={`${kpis.days} jour${kpis.days > 1 ? "s" : ""} affiché${
            kpis.days > 1 ? "s" : ""
          }`}
        />

        <StatMiniCard
          label="Total"
          value={kpis.total.toLocaleString("fr-FR")}
          helper={`Moyenne ${kpis.average.toLocaleString("fr-FR")} visite${
            kpis.average > 1 ? "s" : ""
          } / jour`}
        />

        <StatMiniCard
          label="Aujourd’hui"
          value={kpis.today.toLocaleString("fr-FR")}
          helper="Dernier point de la série"
        />

        <StatMiniCard
          label="Pic"
          value={kpis.max.toLocaleString("fr-FR")}
          helper="Meilleur jour sur la période"
        />
      </div>

      {/* Graphique */}
      <div className="h-[360px] rounded-[24px] border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-950/60">
        {loading ? (
          <div className="flex h-full items-center justify-center text-sm font-medium text-slate-500 dark:text-slate-400">
            Chargement des visites...
          </div>
        ) : filteredData.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm font-medium text-slate-500 dark:text-slate-400">
            Aucune donnée à afficher.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={filteredData}
              margin={{ top: 12, right: 18, left: -10, bottom: 8 }}
            >
              <defs>
                <linearGradient id="visitesGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366F1" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#6366F1" stopOpacity={0.02} />
                </linearGradient>
              </defs>

              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(100,116,139,0.25)"
              />

              <XAxis
                dataKey="date_label"
                tick={{ fontSize: 12 }}
                stroke="rgba(100,116,139,0.9)"
              />

              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 12 }}
                stroke="rgba(100,116,139,0.9)"
              />

              <Tooltip
                labelFormatter={(_, payload) => {
                  const original = payload?.[0]?.payload?.date_visite;
                  return original ? formatLongDate(original) : "";
                }}
                formatter={(value) => [
                  value,
                  smooth ? "Visites lissées" : "Visites",
                ]}
                contentStyle={{
                  borderRadius: 16,
                  border: "1px solid rgba(203,213,225,0.8)",
                  boxShadow: "0 16px 40px rgba(15,23,42,0.12)",
                }}
              />

              <Area
                type="monotone"
                dataKey="value"
                name={smooth ? "Visites lissées" : "Visites"}
                stroke="#6366F1"
                strokeWidth={3}
                fill="url(#visitesGradient)"
                activeDot={{ r: 6 }}
                dot={{ r: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}